import {
  assessSessionMutation,
  AssessmentBlockModified,
  client,
  getCandidatesPublicPublicCandidatesGetInfiniteOptions,
  getDefaultAssessmentComponentsOptions,
} from '@/api';
import Loader from '@/components/Loader';
import { CandidateDetailsSheet } from '@/scenes/Candidates/CandidateDetailsSheet';
import { columns } from '@/scenes/Candidates/columns';
import { DataTable } from '@/components/ShadcnTable/Table';
import {
  cameraFilterAtom,
  emailFilterAtom,
  showArchivedState,
  sortingAtom,
} from '@/components/ShadcnTable/atom';
import { defaultAppStatuses } from '@/constants/form';
import { useAtomValue } from 'jotai';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Target, Settings, Hash, Type, List, X, GraduationCap, Mail, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

export const Route = createFileRoute('/summary/$sessionTemplateId')({
  component: RouteComponent,
});

const ESSENTIAL_BLOCK_KEYS = ['short_summary', 'match_rate'];
const PASSWORD_KEY = 'summary-password';

function readStoredPassword() {
  try {
    return window.sessionStorage.getItem(PASSWORD_KEY) ?? '';
  } catch {
    return '';
  }
}

function storePassword(password: string) {
  try {
    if (password) window.sessionStorage.setItem(PASSWORD_KEY, password);
    else window.sessionStorage.removeItem(PASSWORD_KEY);
  } catch {
    // Private mode: the password just won't survive a reload.
  }
}

function RouteComponent() {
  const [password, setPassword] = useState(readStoredPassword);
  const [rejected, setRejected] = useState(false);

  const unlock = (value: string) => {
    storePassword(value);
    setRejected(false);
    setPassword(value);
  };

  const reject = () => {
    storePassword('');
    setRejected(true);
    setPassword('');
  };

  if (!password) {
    return <PasswordGate rejected={rejected} onSubmit={unlock} />;
  }
  return <SummaryPage password={password} onRejected={reject} />;
}

function PasswordGate({
  rejected,
  onSubmit,
}: {
  rejected: boolean;
  onSubmit: (password: string) => void;
}) {
  const [value, setValue] = useState('');

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <form
        className="w-full max-w-xs space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (value) onSubmit(value);
        }}
      >
        <Label htmlFor="summary-password">Password</Label>
        <Input
          id="summary-password"
          type="password"
          autoFocus
          autoComplete="current-password"
          value={value}
          onChange={(event) => setValue(event.target.value)}
        />
        {rejected && (
          <p className="text-sm text-destructive">That password didn't work.</p>
        )}
        <Button type="submit" className="w-full" disabled={!value}>
          Open
        </Button>
      </form>
    </div>
  );
}

const getTypeIcon = (outputType: string) => {
  switch (outputType) {
    case 'float':
    case 'number':
      return <Hash size={16} className="text-blue-600" />;
    case 'string':
    case 'text':
      return <Type size={16} className="text-yellow-600" />;
    case 'list':
    case 'array':
      return <List size={16} className="text-green-600" />;
    default:
      return <Type size={16} className="text-gray-600" />;
  }
};

function SummaryPage({
  password,
  onRejected,
}: {
  password: string;
  onRejected: () => void;
}) {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [currentCandidateIndex, setCurrentCandidateIndex] = useState(0);
  const [selectedBlockKey, setSelectedBlockKey] = useState<string | null>(null);
  const [customBlocks, setCustomBlocks] = useState<
    AssessmentBlockModified[] | null
  >(null);
  const [generatedPreview, setGeneratedPreview] = useState<any>(null);
  const isPreviewMode = generatedPreview !== null;

  // TODO: Replace with actual admin check
  const isAdmin = true; // This should come from user context/auth

  const { sessionTemplateId } = Route.useParams();
  const [pageSize, setPageSize] = useState(10);
  const [currentTablePage, setCurrentTablePage] = useState(0);
  const emailFilter = useAtomValue(emailFilterAtom);
  const showArchived = useAtomValue(showArchivedState);
  const cameraFilter = useAtomValue(cameraFilterAtom);
  const sorting = useAtomValue(sortingAtom);

  // Same list a company sees on /applications, across every company when the template is 'all'
  const { data, error, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      ...getCandidatesPublicPublicCandidatesGetInfiniteOptions({
        headers: { 'X-Summary-Password': password },
        query: {
          session_template_id:
            sessionTemplateId === 'all' ? undefined : sessionTemplateId,
          page_size: pageSize,
          status: defaultAppStatuses,
          is_archived: showArchived,
          email: emailFilter,
          camera: cameraFilter,
          sort: sorting.length ? sorting[0].id : 'match_rate',
          order: sorting.length && sorting[0].desc ? 'desc' : 'asc',
        },
      }),
      getNextPageParam: (lastPage, pages) => {
        return lastPage.total_pages > pages.length
          ? pages.length + 1
          : undefined;
      },
    });

  // A filter change reloads the list from its first page, so the table should show that page too.
  useEffect(() => {
    setCurrentTablePage(0);
  }, [emailFilter, showArchived, cameraFilter, sorting]);

  const isWrongPassword = error?.response?.status === 401;
  useEffect(() => {
    if (isWrongPassword) onRejected();
  }, [isWrongPassword, onRejected]);

  // Flatten all candidates from all pages
  const allCandidates = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.listings || []);
  }, [data]);

  const totalCandidatesCount = data?.pages?.[0]?.total_pages
    ? data.pages[0].total_pages * pageSize
    : allCandidates.length;

  const currentCandidate = allCandidates[currentCandidateIndex];

  const currentPageCandidates = allCandidates.slice(
    currentTablePage * pageSize,
    (currentTablePage + 1) * pageSize,
  );

  const handleTablePagination = (pagination: { pageIndex: number }) => {
    setCurrentTablePage(pagination.pageIndex);
    // Keep two pages loaded ahead of the one on screen
    if (
      allCandidates.length < (pagination.pageIndex + 3) * pageSize &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  const handleOpenDetails = (candidateId: string) => {
    const index = allCandidates.findIndex((candidate) => candidate.id === candidateId);
    if (index === -1) return;
    setCurrentCandidateIndex(index);
    setGeneratedPreview(null);
    setIsSheetOpen(true);
  };

  // Row selection drives bulk actions, which act as a company member, so the column is left out here
  const tableColumns = columns({ onOpenDetails: handleOpenDetails }).filter(
    (column) => column.id !== 'select',
  );

  const { data: assessmentBlocksData } = useQuery(
    getDefaultAssessmentComponentsOptions(),
  );

  const assessmentBlocks =
    customBlocks ??
    (assessmentBlocksData ?? [])
      .filter((block) => ESSENTIAL_BLOCK_KEYS.includes(block.key))
      .map((block) => ({ ...block, notes: '' }));

  const availableBlocks = (assessmentBlocksData ?? []).filter(
    (block) => !assessmentBlocks.some((b) => b.key === block.key),
  );

  const generatePreview = useMutation({
    ...assessSessionMutation(),
    onSuccess: (data) => {
      setGeneratedPreview(data);
    },
    onError: (error) => {
      console.error('Error generating preview:', error);
    },
  });

  const generateLearningPath = useMutation({
    mutationFn: async (applicationId: string) => {
      const { data } = await client.post({
        url: '/learn/generate-user-learning-path',
        body: { application_id: applicationId },
        headers: { 'Content-Type': 'application/json' },
        responseType: 'json',
      });
      return data as { group_id: string; public_id: string; email_sent: boolean };
    },
    onSuccess: (data) => {
      if (data.email_sent) {
        toast.success('Learning path generated and email sent');
      } else {
        toast.success('Learning path generated (email failed to send)');
      }
    },
    onError: () => {
      toast.error('Failed to generate learning path');
    },
  });

  const handleGenerateLearningPath = () => {
    if (currentCandidate) {
      generateLearningPath.mutate(currentCandidate.id);
    }
  };

  const handleFetchMoreCandidates = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleNavigate = (index: number) => {
    setCurrentCandidateIndex(index);
    setGeneratedPreview(null);

    // Check if we need to fetch more data when navigating near the end
    if (allCandidates.length - index <= pageSize) {
      handleFetchMoreCandidates();
    }
  };

  const handleBlockClick = (blockKey: string) => {
    setSelectedBlockKey(selectedBlockKey === blockKey ? null : blockKey);
  };

  const handleNotesChange = (blockKey: string, notes: string) => {
    setCustomBlocks(
      assessmentBlocks.map((block) =>
        block.key === blockKey ? { ...block, notes } : block,
      ),
    );
  };

  const addAssessmentBlock = (blockToAdd: AssessmentBlockModified) => {
    setCustomBlocks([...assessmentBlocks, blockToAdd]);
  };

  const removeAssessmentBlock = (blockKey: string) => {
    setCustomBlocks(assessmentBlocks.filter((block) => block.key !== blockKey));
  };

  const handleGeneratePreview = () => {
    if (
      currentCandidate &&
      currentCandidate.room_id &&
      assessmentBlocks.length > 0
    ) {
      const enabledBlocks = assessmentBlocks.map((block) => ({
        name: block.name,
        key: block.key,
        required_inputs: block.required_inputs,
        output_type: block.output_type,
        output_instructions: block.output_instructions,
        description: block.description,
        notes: block.notes || '',
      }));

      generatePreview.mutate({
        body: {
          room_id: currentCandidate.room_id,
          enabled_blocks: enabledBlocks,
        },
      });
    }
  };

  const handleBackToOriginal = () => {
    setGeneratedPreview(null);
  };

  // Assessment Configuration Panel (Admin Only)
  const assessmentConfigPanel =
    isAdmin &&
    isSheetOpen &&
    createPortal(
      <div className="fixed top-4 right-4 z-[50] w-80 bg-white/95 backdrop-blur-lg border border-gray-200 rounded-2xl shadow-2xl p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Assessment Preview</h3>
          <Badge
            variant="outline"
            className="bg-purple-50 text-purple-700 border-purple-200">
            Admin
          </Badge>
        </div>

        {/* Assessment Blocks Configuration */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium text-gray-700">
              Assessment Blocks
            </Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2">
                  <Target size={16} />
                  Add Block
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                {availableBlocks.length > 0 ? (
                  availableBlocks.map((block) => (
                    <DropdownMenuItem
                      key={block.key}
                      onClick={() => addAssessmentBlock(block)}
                      className="flex items-start gap-3 p-3 cursor-pointer">
                      <div className="p-1 rounded bg-gray-50">
                        {getTypeIcon(block.output_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-900">
                          {block.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {block.description}
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))
                ) : (
                  <div className="p-3 text-center text-sm text-gray-500">
                    All available blocks have been added
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Selected Blocks */}
          {assessmentBlocks.length > 0 ? (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                {assessmentBlocks.map((block) => (
                  <Tooltip key={block.key}>
                    <TooltipTrigger asChild>
                      <div
                        className={`group relative p-2 bg-white border rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer ${selectedBlockKey === block.key
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}>
                        {!ESSENTIAL_BLOCK_KEYS.includes(block.key) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeAssessmentBlock(block.key);
                            }}
                            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity h-4 w-4 p-0 hover:bg-red-50 hover:text-red-600 text-xs">
                            <X size={12} />
                          </Button>
                        )}
                        <div
                          onClick={() => handleBlockClick(block.key)}
                          className="flex flex-col items-center justify-center text-center space-y-1">
                          <div className="p-1 rounded bg-gray-50">
                            {getTypeIcon(block.output_type)}
                          </div>
                          <h4 className="font-medium text-xs text-gray-900 leading-tight">
                            {block.name}
                          </h4>
                          {block.notes && (
                            <div className="text-xs text-green-700 bg-green-100 px-1 py-0.5 rounded-full">
                              Notes
                            </div>
                          )}
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs p-3">
                      <div className="text-xs text-gray-600">
                        {block.description}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>

              {/* Notes Editor */}
              {selectedBlockKey && (
                <Textarea
                  value={
                    assessmentBlocks.find((b) => b.key === selectedBlockKey)
                      ?.notes || ''
                  }
                  onChange={(e) =>
                    handleNotesChange(selectedBlockKey, e.target.value)
                  }
                  placeholder="Add instructions for this block..."
                  className="min-h-[60px] resize-none text-sm"
                />
              )}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              <Target className="mx-auto h-8 w-8 text-gray-300 mb-2" />
              <p className="text-xs">No assessment blocks added</p>
            </div>
          )}

          {/* Generate and Control Buttons */}
          <div className="space-y-2">
            <Button
              onClick={handleGeneratePreview}
              disabled={
                !currentCandidate ||
                !currentCandidate.room_id ||
                generatePreview.isPending ||
                assessmentBlocks.length === 0
              }
              className="w-full"
              size="sm">
              {generatePreview.isPending ? 'Generating...' : 'Generate Preview'}
            </Button>

            {isPreviewMode && (
              <Button
                onClick={handleBackToOriginal}
                variant="outline"
                className="w-full"
                size="sm">
                Back to Original
              </Button>
            )}
          </div>

          {/* Session debugger: per-turn latency, tools, observer, answers, labels */}
          <div className="pt-2 border-t border-gray-100 flex gap-2">
            {currentCandidate?.room_id && (
              <Button asChild variant="outline" className="flex-1" size="sm">
                <Link to="/reviews/$roomId" params={{ roomId: currentCandidate.room_id }}>
                  Debug timeline
                </Link>
              </Button>
            )}
            <Button asChild variant="outline" className="flex-1" size="sm">
              <Link to="/reviews">Search sessions</Link>
            </Button>
          </div>

          {/* Learning Path Generation */}
          {currentCandidate && (
            <div className="pt-2 border-t border-gray-100">
              <Button
                onClick={handleGenerateLearningPath}
                disabled={generateLearningPath.isPending}
                variant="outline"
                className="w-full"
                size="sm">
                {generateLearningPath.isPending ? (
                  <>
                    <Loader2 size={14} className="animate-spin mr-1" />
                    Generating...
                  </>
                ) : (
                  <>
                    <GraduationCap size={14} className="mr-1" />
                    Generate Learning Path
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-500 mt-1 text-center">
                Generates a path and emails {currentCandidate.email}
              </p>
            </div>
          )}

          {assessmentBlocks.length === 0 && (
            <p className="text-xs text-amber-600 text-center">
              Add assessment blocks to generate preview
            </p>
          )}
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      {assessmentConfigPanel}

      <main className="mx-auto max-w-6xl px-4 py-6">
        <DataTable
          loading={isLoading}
          pageIndex={currentTablePage}
          setPagination={handleTablePagination}
          setPageSize={(size) => {
            setPageSize(size);
            setCurrentTablePage(0);
          }}
          pageSize={pageSize}
          pageCount={data?.pages?.[0]?.total_pages ?? 0}
          data={currentPageCandidates}
          columns={tableColumns}
        />
      </main>

      <CandidateDetailsSheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        candidates={allCandidates}
        currentCandidateIndex={currentCandidateIndex}
        onNavigate={handleNavigate}
        totalCandidates={totalCandidatesCount}
        onFetchMoreCandidates={handleFetchMoreCandidates}
        isFetchingMore={isFetchingNextPage}
        hasMorePages={hasNextPage}
        // Pass preview data if in preview mode
        previewData={isPreviewMode ? generatedPreview : null}
        isPreviewMode={isPreviewMode}
      />
    </>
  );
}
